import { noticeApi } from '../api/noticeApi';
import type { NoticeItem, CreateNoticeRequest, UpdateNoticeRequest, NoticeFilterParams } from '../types/notice';
import { useBaseQuery, useBaseMutation, useNoDataMutation, useInvalidateQueries } from './useBaseQuery';
import { PaginationData } from '../models/paginationData';

export const noticeKeys = {
    all: ['notice'] as const,
    lists: () => [...noticeKeys.all, 'list'] as const,
    list: (params: NoticeFilterParams) => [...noticeKeys.lists(), params] as const,
};

export const useNotices = (params: NoticeFilterParams) => {
    return useBaseQuery<PaginationData<NoticeItem>>(
        noticeKeys.list(params),
        () => noticeApi.getNotices(params),
        { staleTime: 10_000 },
    );
};

export const useCreateNotice = () => {
    const { invalidate } = useInvalidateQueries();

    return useBaseMutation<CreateNoticeRequest, NoticeItem>(
        (data) => noticeApi.create(data),
        {
            onSuccess: () => invalidate(noticeKeys.lists()),
        },
    );
};

export const useUpdateNotice = () => {
    const { invalidate } = useInvalidateQueries();

    return useBaseMutation<{ id: number; data: UpdateNoticeRequest }, NoticeItem>(
        ({ id, data }) => noticeApi.update(id, data),
        {
            onSuccess: () => invalidate(noticeKeys.lists()),
        },
    );
};

export const useDeleteNotice = () => {
    const { invalidate } = useInvalidateQueries();

    return useNoDataMutation<number>(
        (id) => noticeApi.remove(id),
        {
            onSuccess: () => invalidate(noticeKeys.lists()),
        },
    );
};
